import {
    Group,
    Mesh,
    PlaneGeometry,
    MeshBasicMaterial,
    BufferGeometry,
    Float32BufferAttribute,
    TextureLoader,
    Vector3,
    Matrix4,
    Quaternion,
    DoubleSide,
    ShaderMaterial,
    Color,
    CanvasTexture,
    FrontSide,
} from "three";

import { GLOBE_RADIUS, latLngToSphere, morphedXYZ } from "./projections";
import {
    ZOOM_FAR,
    ZOOM_NEAR,
    MARKER_SIZE,
    MARKER_SPREAD,
    CLIP_SPREAD,
    LINE_WIDTH,
    ZOOM_SCALE_MIN,
    LINE_ZOOM_SCALE_MIN,
    CURVE_SEGMENTS_HI,
    CURVE_SEGMENTS_LO,
    LINE_PULSE_EXTRA,
    ARC_HEIGHT_FACTOR,
    LINE_DURATION,
    LINE_PAUSE,
    CLIP_CARD_SCALE,
    CLIP_CARD_STACK_SCALE,
    CHIP_SCALE,
} from "./config";
import {
    clipCardVertexShader,
    clipCardFragmentShader,
} from "./shaders/clipShader";

const CLIP_CARD_SIZE = MARKER_SIZE * CLIP_CARD_SCALE;
const STACK_OFFSET = CLIP_CARD_SIZE * CLIP_CARD_STACK_SCALE;
const CHIP_SIZE = CLIP_CARD_SIZE * CHIP_SCALE;
const DEFAULT_UNLOCKED_ICON =
    "https://speed-cdn.expedia.com/threejs/textures/unlocked_marker.webp";
const DEFAULT_LOCKED_ICON =
    "https://speed-cdn.expedia.com/threejs/textures/locked_marker.webp";
const DEFAULT_SPEED_ICON =
    "https://speed-cdn.expedia.com/threejs/textures/speed_marker.webp";
const DEFAULT_SPEED_HOVER_ICON =
    "https://speed-cdn.expedia.com/threejs/textures/speed_hover_marker.webp";

const _tangent = new Vector3();
const _offset = new Vector3();
const _camDir = new Vector3();
const _surfaceNormal = new Vector3();
const _toCamera = new Vector3();
const _camUp = new Vector3();
const _camRight = new Vector3();
const _chipOffset = new Vector3();
const _lookMatrix = new Matrix4();
const _markerQuat = new Quaternion();
const _localCamPos = new Vector3();
const _parentQuat = new Quaternion();
const _invParentMat = new Matrix4();
const _vpWorldPos = new Vector3();
const _vpNdc = new Vector3();
const _vpCamPos = new Vector3();
const _vpGlobeCenter = new Vector3();
const _vpSurfNorm = new Vector3();
const _vpToCamera = new Vector3();

function interpolateLatLng(
    lat1,
    lng1,
    lat2,
    lng2,
    steps,
    { amplitude = 0, frequency = 1, phase = 0 } = {},
) {
    const toRad = Math.PI / 180;
    const v1 = new Vector3(
        Math.cos(lat1 * toRad) * Math.cos(lng1 * toRad),
        Math.sin(lat1 * toRad),
        Math.cos(lat1 * toRad) * Math.sin(lng1 * toRad),
    );
    const v2 = new Vector3(
        Math.cos(lat2 * toRad) * Math.cos(lng2 * toRad),
        Math.sin(lat2 * toRad),
        Math.cos(lat2 * toRad) * Math.sin(lng2 * toRad),
    );
    const result = [];
    for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const tmp = new Vector3().copy(v1).lerp(v2, t).normalize();

        if (amplitude > 0 && i > 0 && i < steps) {
            const next = new Vector3()
                .copy(v1)
                .lerp(v2, (i + 0.5) / steps)
                .normalize();
            const tangent = next.clone().sub(tmp).normalize();
            const perp = new Vector3().crossVectors(tangent, tmp).normalize();
            const disp =
                amplitude *
                (Math.sin(t * frequency * Math.PI * 2 + phase) * 0.6 +
                    Math.sin(t * frequency * Math.PI * 5 + phase * 1.7) * 0.4);
            tmp.addScaledVector(perp, disp).normalize();
        }

        result.push({
            lat: Math.asin(tmp.y) / toRad,
            lng: Math.atan2(tmp.z, tmp.x) / toRad,
        });
    }
    return result;
}

function buildRibbonGeometry(pointCount) {
    const vertexCount = pointCount * 2;

    const positions = new Float32Array(vertexCount * 3);
    const offsetDirs = new Float32Array(vertexCount * 3);
    const uvs = new Float32Array(vertexCount * 2);
    const indices = [];

    for (let i = 0; i < pointCount; i++) {
        uvs[i * 4 + 0] = 0;
        uvs[i * 4 + 1] = 0;
        uvs[i * 4 + 2] = 0;
        uvs[i * 4 + 3] = 1;
    }

    for (let i = 0; i < pointCount - 1; i++) {
        const a = i * 2;
        indices.push(a, a + 1, a + 2);
        indices.push(a + 1, a + 3, a + 2);
    }

    const geo = new BufferGeometry();
    geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
    geo.setAttribute("aOffsetDir", new Float32BufferAttribute(offsetDirs, 3));
    geo.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
    geo.setIndex(indices);

    return geo;
}

const pulseVertexShader = /* glsl */ `
attribute vec3 aOffsetDir;

uniform float uHeadPos;
uniform float uBaseWidth;
uniform float uPulseExtra;
uniform float uPulseWidth;
uniform float uPulseRise;
uniform vec3 uCameraDir;

varying vec2 vUv;
varying float vVisibility;

void main() {
    vUv = uv;

    float side = uv.y * 2.0 - 1.0;
    float dist = uv.x;

    float distBehindHead = uHeadPos - dist;
    float pulse = smoothstep(uPulseWidth, 0.0, distBehindHead) * step(0.0, distBehindHead);

    float width = uBaseWidth + uPulseExtra * pulse;

    vec3 pos = position + aOffsetDir * width * side;
    vec3 surfNormal = normalize(position);
    pos += surfNormal * uPulseRise * pulse;

    vVisibility = dot(surfNormal, uCameraDir);

    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
}
`;

const pulseFragmentShader = /* glsl */ `
uniform float uHeadPos;
uniform vec3 uColor;
uniform vec3 uPulseColor;
uniform float uPulseWidth;
uniform float uProgress;
uniform float uLineOpacity;

varying vec2 vUv;
varying float vVisibility;

void main() {
    float dist = vUv.x;

    float distBehindHead = uHeadPos - dist;
    float pulse = smoothstep(uPulseWidth, 0.0, distBehindHead) * step(0.0, distBehindHead);

    vec3 color = mix(uColor, uPulseColor, pulse * 0.9);

    float fadeOpacity = smoothstep(0.0, 0.05, uProgress);
    float alpha = mix(uLineOpacity, 1.0, pulse) * fadeOpacity;
    gl_FragColor = vec4(color, alpha);
}
`;

export default class GlobeMarkers {
    constructor() {
        this.markersGroup = new Group();
        this.linesGroup = new Group();

        this._showMarkers = true;
        this._showLines = true;
        this._showLocked = true;

        this._markers = [];
        this._continentLines = [];
        this._lineGroups = [];

        this._localCameraDir = new Vector3();
        this._zoomMult = 1;
        this._lineZoomMult = 1;

        this._pinnedCities = [];
        this._continentOrder = {};
        this._countryMap = new Map();
        this._dataLoaded = false;
        this._texturesLoaded = false;

        this._textureLoader = new TextureLoader();
        this._unlockedTexture = null;
        this._lockedTexture = null;

        this._markerGeo = new PlaneGeometry(MARKER_SIZE, MARKER_SIZE);
        this._clipCardGeo = new PlaneGeometry(CLIP_CARD_SIZE, CLIP_CARD_SIZE);
        this._chipGeo = new PlaneGeometry(CHIP_SIZE, CHIP_SIZE);

        this._clipCards = [];
        this._clipMode = false;
        this._isFocused = false;
        this._activeClipCity = null;
        this._hiddenForClips = new Set();
        this._visibleCitiesSet = new Set();
        this._newVisibleSet = new Set();
        this._existingSet = new Set();
        this._isLowPower = false;
        this._markersHidden = false;
        this._proximityOffsets = new Map();
        this._placeholderTexture = this._buildPlaceholderTexture();

        this._loadTextures();
    }

    loadData(pinnedCities, continentOrder, isLowPower = false) {
        this._isLowPower = isLowPower;
        this._pinnedCities = pinnedCities;
        this._continentOrder = continentOrder;
        this._countryMap = new Map(pinnedCities.map((c) => [c.name, c]));
        this._dataLoaded = true;
        this._computeProximityOffsets();
        this._buildContinentLines();
        if (this._texturesLoaded) this._buildMarkers();
    }

    _computeProximityOffsets() {
        const DEG_TO_RAD = Math.PI / 180;
        const MIN_SEP_DEG =
            (MARKER_SIZE * MARKER_SPREAD) / (GLOBE_RADIUS * DEG_TO_RAD);
        const cities = this._pinnedCities;
        const n = cities.length;
        const latOff = new Float64Array(n);
        const lngOff = new Float64Array(n);

        for (let iter = 0; iter < 20; iter++) {
            for (let i = 0; i < n; i++) {
                for (let j = i + 1; j < n; j++) {
                    const lai = cities[i].lat + latOff[i];
                    const lni = cities[i].lng + lngOff[i];
                    const laj = cities[j].lat + latOff[j];
                    const lnj = cities[j].lng + lngOff[j];

                    const cosLat = Math.cos((lai + laj) * 0.5 * DEG_TO_RAD);
                    const dLat = lai - laj;
                    const dLng = (lni - lnj) * cosLat;
                    const dist = Math.sqrt(dLat * dLat + dLng * dLng);

                    if (dist >= MIN_SEP_DEG) continue;

                    const push = (MIN_SEP_DEG - dist) * 0.5;
                    let nx, ny;
                    if (dist < 0.001) {
                        const ang = ((j * 137.508) % 360) * DEG_TO_RAD;
                        nx = Math.cos(ang);
                        ny = Math.sin(ang);
                    } else {
                        nx = dLat / dist;
                        ny = dLng / dist;
                    }

                    latOff[i] += nx * push;
                    lngOff[i] += (ny * push) / Math.max(cosLat, 0.01);
                    latOff[j] -= nx * push;
                    lngOff[j] -= (ny * push) / Math.max(cosLat, 0.01);
                }
            }
        }

        this._proximityOffsets.clear();
        for (let i = 0; i < n; i++) {
            this._proximityOffsets.set(cities[i].name, {
                lat: latOff[i],
                lng: lngOff[i],
            });
        }
    }

    async _loadTextures() {
        try {
            this._unlockedTexture = await this._textureLoader.loadAsync(
                DEFAULT_UNLOCKED_ICON,
            );
            this._lockedTexture =
                await this._textureLoader.loadAsync(DEFAULT_LOCKED_ICON);
            this._speedTexture =
                await this._textureLoader.loadAsync(DEFAULT_SPEED_ICON);
            this._speedHoverTexture = await this._textureLoader.loadAsync(
                DEFAULT_SPEED_HOVER_ICON,
            );
        } catch (err) {
            console.error("[GlobeMarkers] Failed to load icon textures:", err);
        }
        this._texturesLoaded = true;
        if (this._dataLoaded) this._buildMarkers();
    }

    _buildMarkers() {
        this._clearMarkers();

        for (const country of this._pinnedCities) {
            const isSpeed = !!country.speed;
            const isLocked = !!country.locked;
            const texture = isSpeed
                ? this._speedTexture
                : isLocked
                  ? this._lockedTexture
                  : this._unlockedTexture;

            const mat = new MeshBasicMaterial({
                map: texture,
                transparent: true,
                depthTest: false,
                depthWrite: false,
                side: DoubleSide,
                color: 0xffffff,
            });

            const mesh = new Mesh(this._markerGeo, mat);
            mesh.renderOrder = isSpeed ? 99 : 10;
            mesh.scale.setScalar(0);
            mesh.userData.countryName = country.name;
            this.markersGroup.add(mesh);

            let hoverMesh = null;
            if (isSpeed && this._speedHoverTexture) {
                const hoverMat = new MeshBasicMaterial({
                    map: this._speedHoverTexture,
                    transparent: true,
                    depthTest: false,
                    depthWrite: false,
                    side: DoubleSide,
                    color: 0xffffff,
                    opacity: 0,
                });
                hoverMesh = new Mesh(this._markerGeo, hoverMat);
                hoverMesh.raycast = () => {};
                this.markersGroup.add(hoverMesh);
            }

            const _off = this._proximityOffsets.get(country.name);
            this._markers.push({
                mesh,
                hoverMesh,
                country,
                isSpeed,
                isLocked,
                scale: 0,
                scaleTarget: 0,
                hoverMult: 1,
                hoverMultTarget: 1,
                hoverOpacity: 0,
                hoverOpacityTarget: 0,
                delay: 0,
                isActive: false,
                isHovered: false,
                _latOffset: _off?.lat ?? 0,
                _lngOffset: _off?.lng ?? 0,
            });
        }
    }

    _clearMarkers() {
        for (const { mesh, hoverMesh } of this._markers) {
            this.markersGroup.remove(mesh);
            mesh.material.dispose();
            if (hoverMesh) {
                this.markersGroup.remove(hoverMesh);
                hoverMesh.material.dispose();
            }
        }
        this._markers = [];
    }

    _buildContinentLines() {
        this._clearContinentLines();

        const groups = this._buildContinentGroups();

        for (const group of groups) {
            const lineGroup = { lines: [] };

            for (let i = 0; i < group.length - 1; i++) {
                const curveSegments = this._isLowPower
                    ? CURVE_SEGMENTS_LO
                    : CURVE_SEGMENTS_HI;
                const allPoints = interpolateLatLng(
                    group[i].lat,
                    group[i].lng,
                    group[i + 1].lat,
                    group[i + 1].lng,
                    curveSegments,
                );

                const toRad = Math.PI / 180;
                const _v1 = new Vector3(
                    Math.cos(group[i].lat * toRad) *
                        Math.cos(group[i].lng * toRad),
                    Math.sin(group[i].lat * toRad),
                    Math.cos(group[i].lat * toRad) *
                        Math.sin(group[i].lng * toRad),
                ).normalize();
                const _v2 = new Vector3(
                    Math.cos(group[i + 1].lat * toRad) *
                        Math.cos(group[i + 1].lng * toRad),
                    Math.sin(group[i + 1].lat * toRad),
                    Math.cos(group[i + 1].lat * toRad) *
                        Math.sin(group[i + 1].lng * toRad),
                ).normalize();
                const gcAngle = Math.acos(
                    Math.max(-1, Math.min(1, _v1.dot(_v2))),
                );
                const archHeight = gcAngle * ARC_HEIGHT_FACTOR;

                const geo = buildRibbonGeometry(allPoints.length);

                const mat = new ShaderMaterial({
                    vertexShader: pulseVertexShader,
                    fragmentShader: pulseFragmentShader,
                    uniforms: {
                        uHeadPos: { value: -999.0 },
                        uColor: {
                            value: new Color(0xffe070).convertLinearToSRGB(),
                        },
                        uPulseColor: {
                            value: new Color(0xffce1f).convertLinearToSRGB(),
                        },
                        uPulseWidth: { value: 0.25 },
                        uTotalLength: { value: 0.2 },
                        uBaseWidth: { value: LINE_WIDTH },
                        uPulseExtra: { value: LINE_PULSE_EXTRA * 2.5 },
                        uPulseRise: { value: 0.005 },
                        uProgress: { value: 0 },
                        uLineOpacity: { value: 0.0 },
                        uCameraDir: { value: new Vector3(0, 0, 1) },
                    },
                    transparent: true,
                    depthWrite: false,
                    side: DoubleSide,
                });

                const mesh = new Mesh(geo, mat);
                mesh.frustumCulled = false;
                mesh.renderOrder = 5;

                this.linesGroup.add(mesh);
                const lineEntry = {
                    cityA: group[i].name,
                    cityB: group[i + 1].name,
                    obj: mesh,
                    geometry: geo,
                    points: allPoints,
                    material: mat,
                    archHeight,
                    totalDist: 0,
                    centersBuffer: new Float32Array(allPoints.length * 3),
                    distsBuffer: new Float32Array(allPoints.length + 1),
                    lineProgress: 0,
                    lineProgressTarget: 0,
                    lineTime: Math.random() * (LINE_DURATION + LINE_PAUSE),
                };
                lineGroup.lines.push(lineEntry);
                this._continentLines.push(lineEntry);
            }

            if (lineGroup.lines.length > 0) this._lineGroups.push(lineGroup);
        }
    }

    _buildContinentGroups() {
        return Object.values(this._continentOrder)
            .map((order) => {
                return order
                    .filter((name) => {
                        const c = this._countryMap.get(name);
                        if (!c) return false;
                        if (c.locked && !this._showLocked) return false;
                        return true;
                    })
                    .map((name) => {
                        const c = this._countryMap.get(name);
                        const off = this._proximityOffsets.get(name);
                        return {
                            lat: c.lat + (off?.lat ?? 0),
                            lng: c.lng + (off?.lng ?? 0),
                            name,
                        };
                    });
            })
            .filter((group) => group.length >= 2);
    }

    _clearContinentLines() {
        for (const { obj, geometry } of this._continentLines) {
            this.linesGroup.remove(obj);
            geometry.dispose();
            obj.material.dispose();
        }
        this._continentLines = [];
        this._lineGroups = [];
    }

    _buildPlaceholderTexture() {
        const canvas = document.createElement("canvas");
        canvas.width = 128;
        canvas.height = 128;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#1a2d3e";
        ctx.fillRect(0, 0, 128, 128);
        ctx.fillStyle = "#243a4e";
        ctx.fillRect(0, 0, 128, 22);
        ctx.fillRect(0, 106, 128, 22);
        ctx.fillStyle = "#1a2d3e";
        for (let x = 6; x < 120; x += 18) {
            ctx.fillRect(x, 4, 10, 14);
            ctx.fillRect(x, 110, 10, 14);
        }
        ctx.fillStyle = "#2e5068";
        ctx.beginPath();
        ctx.moveTo(50, 40);
        ctx.lineTo(50, 88);
        ctx.lineTo(88, 64);
        ctx.closePath();
        ctx.fill();
        return new CanvasTexture(canvas);
    }

    _texAspect(texture) {
        const img = texture?.image;
        if (!img || !img.height) return 1.0;
        return img.width / img.height;
    }

    _createClipCardMaterial(thumbnailTexture, borderColor) {
        return new ShaderMaterial({
            vertexShader: clipCardVertexShader,
            fragmentShader: clipCardFragmentShader,
            uniforms: {
                uThumbnail: { value: thumbnailTexture },
                uBorderColor: {
                    value: new Color(
                        borderColor[0],
                        borderColor[1],
                        borderColor[2],
                    ),
                },
                uBorderWidth: { value: 0.08 },
                uCornerRadius: { value: 0.25 },
                uOpacity: { value: 0 },
                uTexAspect: { value: this._texAspect(thumbnailTexture) },
            },
            transparent: true,
            depthTest: false,
            depthWrite: false,
            side: FrontSide,
        });
    }

    _buildChipTexture(count) {
        const canvas = document.createElement("canvas");
        canvas.width = 64;
        canvas.height = 64;

        const ctx = canvas.getContext("2d");

        ctx.fillStyle = "#000000";
        ctx.beginPath();
        ctx.arc(32, 32, 28, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = "#FFD60A";
        ctx.font = "bold 30px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(String(count), 32, 33);

        return new CanvasTexture(canvas);
    }

    _isInViewport(lat, lng, morphT, camera) {
        const pos = morphedXYZ(lat, lng, GLOBE_RADIUS * 1.005, morphT);
        _vpWorldPos.set(pos[0], pos[1], pos[2]);
        const parent = this.markersGroup.parent;

        if (parent) _vpWorldPos.applyMatrix4(parent.matrixWorld);

        _vpNdc.copy(_vpWorldPos).project(camera);
        if (Math.abs(_vpNdc.x) > 1 || Math.abs(_vpNdc.y) > 1 || _vpNdc.z > 1)
            return false;

        camera.getWorldPosition(_vpCamPos);
        if (parent) parent.getWorldPosition(_vpGlobeCenter);
        else _vpGlobeCenter.set(0, 0, 0);

        _vpSurfNorm.copy(_vpWorldPos).sub(_vpGlobeCenter).normalize();
        _vpToCamera.copy(_vpCamPos).sub(_vpWorldPos).normalize();
        return _vpSurfNorm.dot(_vpToCamera) > 0.1;
    }

    _buildClipCard(city, thumbnailTexture) {
        const clipCount = city.clips.length;
        const maxStack = this._isLowPower ? 1 : 3;
        const stackCount = Math.min(clipCount, maxStack);
        const meshes = [];

        for (let i = stackCount - 1; i >= 0; i--) {
            const isMain = i === 0;
            const size = 1 - i * 0.1;

            const mat = this._createClipCardMaterial(thumbnailTexture, [
                size,
                size,
                size,
            ]);
            const mesh = new Mesh(this._clipCardGeo, mat);

            mesh.renderOrder = isMain ? 14 : 13 - (i - 1);
            mesh.userData.countryName = city.name;
            mesh.userData.isClipCard = true;
            mesh.userData.stackIndex = i;

            if (!isMain) mesh.raycast = () => {};
            mesh.scale.setScalar(0);

            this.markersGroup.add(mesh);

            meshes.unshift(mesh);
        }

        let chipMesh = null;
        if (clipCount > 1) {
            const chipTex = this._buildChipTexture(clipCount);
            const chipMat = new MeshBasicMaterial({
                map: chipTex,
                transparent: true,
                alphaTest: 0.01,
                depthTest: false,
                depthWrite: false,
                side: DoubleSide,
            });

            chipMesh = new Mesh(this._chipGeo, chipMat);
            chipMesh.renderOrder = 15;
            chipMesh.frustumCulled = false;
            chipMesh.raycast = () => {};
            chipMesh.scale.setScalar(0);

            this.markersGroup.add(chipMesh);
        }

        this._clipCards.push({
            city,
            meshes,
            chipMesh,
            scale: 0,
            scaleTarget: 1,
            delay: 0,
            thumbnailTexture,
            isPlaceholder: true,
            isDying: false,
            normalColor: [1, 1, 1],
            hoverColor: [1, 0.1, 0.1],
            currentColor: [1, 1, 1],
            isHovered: false,
            isActive: this._activeClipCity === city.name,
            _latOffset: 0,
            _lngOffset: 0,
        });
    }

    _computeClipCardProximityOffsets() {
        const DEG_TO_RAD = Math.PI / 180;
        const CLIP_SIZE = MARKER_SIZE * (CLIP_CARD_SCALE ?? 1);
        const MIN_SEP_DEG =
            (CLIP_SIZE * CLIP_SPREAD) / (GLOBE_RADIUS * DEG_TO_RAD);

        const active = this._clipCards.filter((c) => !c.isDying);
        for (const c of active) {
            c._latOffset = 0;
            c._lngOffset = 0;
        }
        const n = active.length;
        if (n < 2) return;

        for (let iter = 0; iter < 20; iter++) {
            for (let i = 0; i < n; i++) {
                for (let j = i + 1; j < n; j++) {
                    const ci = active[i];
                    const cj = active[j];

                    const lai = ci.city.lat + ci._latOffset;
                    const lni = ci.city.lng + ci._lngOffset;
                    const laj = cj.city.lat + cj._latOffset;
                    const lnj = cj.city.lng + cj._lngOffset;

                    const cosLat = Math.cos((lai + laj) * 0.5 * DEG_TO_RAD);
                    const dLat = lai - laj;
                    const dLng = (lni - lnj) * cosLat;
                    const dist = Math.sqrt(dLat * dLat + dLng * dLng);

                    if (dist >= MIN_SEP_DEG) continue;

                    const push = (MIN_SEP_DEG - dist) * 0.5;
                    let nx, ny;
                    if (dist < 0.001) {
                        const ang = ((j * 137.508) % 360) * DEG_TO_RAD;
                        nx = Math.cos(ang);
                        ny = Math.sin(ang);
                    } else {
                        nx = dLat / dist;
                        ny = dLng / dist;
                    }

                    ci._latOffset += nx * push;
                    ci._lngOffset += (ny * push) / Math.max(cosLat, 0.01);
                    cj._latOffset -= nx * push;
                    cj._lngOffset -= (ny * push) / Math.max(cosLat, 0.01);
                }
            }
        }
    }

    _addClipCard(city) {
        const marker = this._markers.find((m) => m.country.name === city.name);
        if (marker) {
            marker.scaleTarget = 0;
            marker.delay = 0;
        }
        this._hiddenForClips.add(city.name);
        this._buildClipCard(city, this._placeholderTexture);
        this._computeClipCardProximityOffsets();

        this._textureLoader
            .loadAsync(city.clips[0].thumb)
            .then((tex) => {
                if (!this._clipMode) {
                    tex.dispose();
                    return;
                }

                const card = this._clipCards.find(
                    (c) => c.city.name === city.name && !c.isDying,
                );
                if (!card || !card.isPlaceholder) {
                    tex.dispose();
                    return;
                }
                card.thumbnailTexture = tex;
                card.isPlaceholder = false;
                const aspect = this._texAspect(tex);
                for (const mesh of card.meshes) {
                    mesh.material.uniforms.uThumbnail.value = tex;
                    mesh.material.uniforms.uTexAspect.value = aspect;
                }
            })
            .catch(() => {});
    }

    enterClipMode(camera, morphT) {
        if (this._clipMode) {
            this._updateClipModeViewport(camera, morphT);
            return;
        }

        this._clipMode = true;

        for (const city of this._pinnedCities) {
            if (city.locked || !city.clips?.length) continue;
            if (!this._isInViewport(city.lat, city.lng, morphT, camera))
                continue;
            this._addClipCard(city);
        }
    }

    _updateClipModeViewport(camera, morphT) {
        this._newVisibleSet.clear();
        for (const city of this._pinnedCities) {
            if (
                !city.locked &&
                city.clips?.length > 0 &&
                this._isInViewport(city.lat, city.lng, morphT, camera)
            ) {
                this._newVisibleSet.add(city.name);
            }
        }

        for (const card of this._clipCards) {
            if (card.isDying) continue;
            if (!this._newVisibleSet.has(card.city.name)) {
                card.scaleTarget = 0;
                card.isDying = true;
                this._hiddenForClips.delete(card.city.name);
                const m = this._markers.find(
                    (m) => m.country.name === card.city.name,
                );
                if (m) {
                    m.scaleTarget = this._isFocused ? 0.75 : 1;
                    m.delay = 0;
                }
            }
        }

        this._existingSet.clear();
        for (const c of this._clipCards) {
            if (!c.isDying) this._existingSet.add(c.city.name);
        }
        for (const city of this._pinnedCities) {
            if (city.locked || !city.clips?.length) continue;
            if (!this._newVisibleSet.has(city.name)) continue;
            if (this._existingSet.has(city.name)) continue;
            this._addClipCard(city);
        }
    }

    exitClipMode() {
        if (!this._clipMode) return;
        this._clipMode = false;

        for (const card of this._clipCards) {
            if (card.isDying) continue;
            card.scaleTarget = 0;
            card.isDying = true;
        }

        for (const name of this._hiddenForClips) {
            const m = this._markers.find((m) => m.country.name === name);
            if (m) {
                m.scaleTarget = this._isFocused ? 0.75 : 1;
                m.delay = 0;
            }
        }
        this._hiddenForClips.clear();
    }

    getClipCardMeshes() {
        return this._clipCards
            .filter((c) => c.scale > 0.01 && !c.isDying)
            .map((c) => c.meshes[0]);
    }

    setHoveredClipCard(countryName) {
        for (const card of this._clipCards) {
            card.isHovered = card.city.name === countryName;
        }
    }

    setActiveClipCard(countryName) {
        this._activeClipCity = countryName ?? null;
        for (const card of this._clipCards) {
            card.isActive = card.city.name === countryName;
        }
    }

    show() {
        this._markersHidden = false;
        for (let i = 0; i < this._markers.length; i++) {
            const m = this._markers[i];
            if (this._hiddenForClips.has(m.country.name)) continue;
            m.scaleTarget = 1;
            m.delay = 0;
        }
    }

    hide() {
        this._markersHidden = true;
        for (const m of this._markers) {
            m.scaleTarget = 0;
            m.delay = 0;
        }
        for (const line of this._continentLines) {
            line.lineProgress = 0;
            line.lineProgressTarget = 0;
        }
    }

    focusOn() {
        this._isFocused = true;
        for (const m of this._markers) {
            if (this._hiddenForClips.has(m.country.name)) continue;
            m.scaleTarget = 0.75;
            m.delay = 0;
        }
    }

    clearFocus() {
        this._isFocused = false;
        for (let i = 0; i < this._markers.length; i++) {
            const m = this._markers[i];
            if (this._hiddenForClips.has(m.country.name)) continue;
            m.scaleTarget = 1;
            m.delay = 0;
        }
    }

    getMarkerMeshes() {
        return this._markers
            .filter(
                (m) =>
                    m.scale > 0.01 &&
                    (!m.isLocked || this._showLocked) &&
                    !this._hiddenForClips.has(m.country.name),
            )
            .map((m) => m.mesh);
    }

    setHoveredMarker(countryName) {
        for (const m of this._markers) {
            const isHovered = m.country.name === countryName;
            m.isHovered = isHovered;
            m.hoverMultTarget = isHovered ? 1.35 : 1;
            if (m.isSpeed) {
                m.hoverOpacityTarget = isHovered ? 1 : 0;
            }
        }
    }

    setActiveMarker(countryName) {
        for (const m of this._markers) {
            m.isActive = m.country.name === countryName;
        }
    }

    _updateVisibility(delta) {
        const MARKER_SPEED = 12;
        const HOVER_SPEED = 16;
        const LINE_SPEED = 3;

        for (const m of this._markers) {
            if (m.delay > 0) {
                m.delay -= delta;
                continue;
            }
            const diff = m.scaleTarget - m.scale;
            if (Math.abs(diff) > 0.001) {
                m.scale += diff * Math.min(MARKER_SPEED * delta, 1);
            } else {
                m.scale = m.scaleTarget;
            }

            const hDiff = m.hoverMultTarget - m.hoverMult;
            if (Math.abs(hDiff) > 0.001) {
                m.hoverMult += hDiff * Math.min(HOVER_SPEED * delta, 1);
            } else {
                m.hoverMult = m.hoverMultTarget;
            }

            const finalScale = m.scale * m.hoverMult * this._zoomMult;
            m.mesh.scale.setScalar(finalScale);
            const elevated = (m.isActive || m.isHovered) && m.scale > 0.01;

            m.mesh.renderOrder = elevated ? 100 : m.isSpeed ? 99 : 10;
            if (m.hoverMesh)
                m.hoverMesh.renderOrder = elevated ? 101 : m.isSpeed ? 100 : 11;

            if (m.hoverMesh) {
                const oDiff = m.hoverOpacityTarget - m.hoverOpacity;
                if (Math.abs(oDiff) > 0.001) {
                    m.hoverOpacity += oDiff * Math.min(HOVER_SPEED * delta, 1);
                } else {
                    m.hoverOpacity = m.hoverOpacityTarget;
                }
                m.hoverMesh.scale.setScalar(finalScale * 1.5);
                m.hoverMesh.material.opacity =
                    m.hoverOpacity * m.mesh.material.opacity;
            }
        }

        this._clipCards = this._clipCards.filter((card) => {
            if (card.delay > 0) {
                card.delay -= delta;
                return true;
            }
            const diff = card.scaleTarget - card.scale;
            if (Math.abs(diff) > 0.001) {
                card.scale += diff * Math.min(12 * delta, 1);
            } else {
                card.scale = card.scaleTarget;
            }

            const targetColor =
                card.isHovered || card.isActive
                    ? card.hoverColor
                    : card.normalColor;
            const colorSpeed = Math.min(8 * delta, 1);
            let colorChanged = false;
            for (let ci = 0; ci < 3; ci++) {
                const diff = targetColor[ci] - card.currentColor[ci];
                if (Math.abs(diff) > 0.001) {
                    card.currentColor[ci] += diff * colorSpeed;
                    colorChanged = true;
                } else {
                    card.currentColor[ci] = targetColor[ci];
                }
            }
            if (colorChanged) {
                for (const mesh of card.meshes) {
                    mesh.material.uniforms.uBorderColor.value.setRGB(
                        card.currentColor[0],
                        card.currentColor[1],
                        card.currentColor[2],
                    );
                }
            }
            const cardElevated = card.isHovered || card.isActive;
            for (let mi = 0; mi < card.meshes.length; mi++) {
                card.meshes[mi].renderOrder = cardElevated
                    ? 20 + card.meshes.length - mi
                    : mi === 0
                      ? 14
                      : 13 - (mi - 1);
            }
            if (card.chipMesh)
                card.chipMesh.renderOrder = cardElevated
                    ? 20 + card.meshes.length + 1
                    : 15;

            if (card.isDying && card.scale < 0.001) {
                for (const mesh of card.meshes) {
                    this.markersGroup.remove(mesh);
                    mesh.material.dispose();
                }
                if (card.chipMesh) {
                    this.markersGroup.remove(card.chipMesh);
                    card.chipMesh.material.map?.dispose();
                    card.chipMesh.material.dispose();
                }
                if (!card.isPlaceholder) card.thumbnailTexture.dispose();
                return false;
            }
            return true;
        });

        let visibleCities = null;
        if (this._clipMode) {
            this._visibleCitiesSet.clear();
            for (const card of this._clipCards) {
                if (!card.isDying) this._visibleCitiesSet.add(card.city.name);
            }
            for (const m of this._markers) {
                if (
                    !this._hiddenForClips.has(m.country.name) &&
                    m.scale > 0.01
                ) {
                    this._visibleCitiesSet.add(m.country.name);
                }
            }
            visibleCities = this._visibleCitiesSet;
        }

        for (const line of this._continentLines) {
            const shouldShow =
                !this._markersHidden &&
                (visibleCities === null ||
                    visibleCities.has(line.cityA) ||
                    visibleCities.has(line.cityB));
            line.lineProgressTarget = shouldShow ? 1 : 0;

            const diff = line.lineProgressTarget - line.lineProgress;
            if (Math.abs(diff) > 0.001) {
                line.lineProgress += diff * Math.min(LINE_SPEED * delta, 1);
            } else {
                line.lineProgress = line.lineProgressTarget;
            }
            line.material.uniforms.uProgress.value = line.lineProgress;
        }
    }

    setShowMarkers(show) {
        this._showMarkers = show;
        this.markersGroup.visible = show;
        this.linesGroup.visible = show && this._showLines;
    }

    setShowLines(show) {
        this._showLines = show;
        this.linesGroup.visible = show;
    }

    setShowLocked(show) {
        if (this._showLocked === show) return;
        this._showLocked = show;

        for (const { mesh, isLocked } of this._markers) {
            if (isLocked) mesh.visible = show;
        }

        this._buildContinentLines();
    }

    update(morphT, camera, delta) {
        if (camera) {
            const d = camera.position.length();
            const t = Math.max(
                0,
                Math.min(1, (d - ZOOM_NEAR) / (ZOOM_FAR - ZOOM_NEAR)),
            );
            this._zoomMult = ZOOM_SCALE_MIN + t * (1 - ZOOM_SCALE_MIN);
            this._lineZoomMult =
                LINE_ZOOM_SCALE_MIN + t * (1 - LINE_ZOOM_SCALE_MIN);
        }
        this._updateVisibility(delta);
        this._updateMarkerPositions(morphT, camera);
        this._updateContinentLines(morphT, delta);
    }

    _updateMarkerPositions(t, camera) {
        if (!camera) return;

        const camWorldPos = _toCamera;
        camera.getWorldPosition(camWorldPos);

        const parentMatrixInv = _lookMatrix;
        const parent = this.markersGroup.parent;
        if (parent) {
            parentMatrixInv.copy(parent.matrixWorld).invert();
        } else {
            parentMatrixInv.identity();
        }

        _localCamPos.copy(camWorldPos).applyMatrix4(parentMatrixInv);
        this._localCameraDir.copy(_localCamPos).normalize();

        _markerQuat.copy(camera.quaternion);
        const parentQuat =
            this.markersGroup.parent?.getWorldQuaternion(_parentQuat) ?? null;
        if (parentQuat) {
            _markerQuat
                .copy(camera.quaternion)
                .premultiply(_parentQuat.invert());
        } else {
            _markerQuat.copy(camera.quaternion);
        }

        _camUp.set(0, 1, 0).applyQuaternion(camera.quaternion);
        const parentMat = this.markersGroup.parent?.matrixWorld;
        if (parentMat) {
            _camUp.transformDirection(_invParentMat.copy(parentMat).invert());
        }

        for (const m of this._markers) {
            const { mesh, hoverMesh, country, isLocked } = m;
            if (isLocked && !this._showLocked) continue;

            const { lat, lng } = country;
            const radius = GLOBE_RADIUS * 1.005;
            const aLat = lat + m._latOffset;
            const aLng = lng + m._lngOffset;

            const pos = morphedXYZ(aLat, aLng, radius, t);
            mesh.position.set(pos[0], pos[1], pos[2]);

            const globePos = latLngToSphere(aLat, aLng, GLOBE_RADIUS);
            _surfaceNormal
                .set(globePos[0], globePos[1], globePos[2])
                .normalize();
            _toCamera.copy(_localCamPos).sub(mesh.position).normalize();

            const visFade = Math.max(
                0,
                Math.min(1, (_toCamera.dot(_surfaceNormal) + 0.1) / 0.25),
            );
            mesh.material.opacity = visFade * visFade * (3 - 2 * visFade);

            mesh.quaternion.copy(_markerQuat);

            if (hoverMesh) {
                hoverMesh.position
                    .copy(mesh.position)
                    .addScaledVector(_camUp, MARKER_SIZE * 0.75);
                hoverMesh.quaternion.copy(_markerQuat);
            }
        }

        if (this._clipCards.length > 0) {
            _camRight.set(1, 0, 0).applyQuaternion(camera.quaternion);
            if (parentMat) {
                _camRight.transformDirection(
                    _invParentMat.copy(parentMat).invert(),
                );
            }

            for (const card of this._clipCards) {
                const { lat, lng } = card.city;
                const aLat = lat + card._latOffset;
                const aLng = lng + card._lngOffset;
                const pos = morphedXYZ(aLat, aLng, GLOBE_RADIUS * 1.008, t);

                const surfPos = morphedXYZ(aLat, aLng, GLOBE_RADIUS, t);
                _surfaceNormal
                    .set(surfPos[0], surfPos[1], surfPos[2])
                    .normalize();
                _toCamera
                    .set(pos[0], pos[1], pos[2])
                    .negate()
                    .add(_localCamPos)
                    .normalize();
                const visFade = Math.max(
                    0,
                    Math.min(1, (_toCamera.dot(_surfaceNormal) + 0.1) / 0.25),
                );
                const finalOpacity = visFade * visFade * (3 - 2 * visFade);

                for (let i = 0; i < card.meshes.length; i++) {
                    const mesh = card.meshes[i];
                    mesh.position.set(
                        pos[0] - _camUp.x * STACK_OFFSET * i,
                        pos[1] - _camUp.y * STACK_OFFSET * i,
                        pos[2] - _camUp.z * STACK_OFFSET * i,
                    );
                    mesh.quaternion.copy(_markerQuat);
                    mesh.scale.setScalar(
                        card.scale * (1 - i * 0.15) * this._zoomMult,
                    );
                    mesh.material.uniforms.uOpacity.value = finalOpacity;
                }

                if (card.chipMesh) {
                    _chipOffset
                        .copy(_camUp)
                        .multiplyScalar(-(CLIP_CARD_SIZE * 0.3));

                    card.chipMesh.position.set(
                        pos[0] + _chipOffset.x,
                        pos[1] + _chipOffset.y,
                        pos[2] + _chipOffset.z,
                    );

                    card.chipMesh.quaternion.copy(_markerQuat);
                    card.chipMesh.scale.setScalar(card.scale * this._zoomMult);
                    card.chipMesh.material.opacity = finalOpacity;
                }
            }
        }
    }

    _updateContinentLines(t, delta) {
        const d = delta || 0.016;
        const baseRadius = GLOBE_RADIUS * 1.005;
        const lineWidth = LINE_WIDTH * this._lineZoomMult;
        const pulseExtra = LINE_PULSE_EXTRA * 2.5 * this._lineZoomMult;

        for (const lineGroup of this._lineGroups) {
            for (const line of lineGroup.lines) {
                if (line.lineProgress <= 0) continue;

                const {
                    geometry,
                    points,
                    material,
                    centersBuffer,
                    distsBuffer,
                    archHeight,
                } = line;
                material.uniforms.uCameraDir.value.copy(this._localCameraDir);
                material.uniforms.uBaseWidth.value = lineWidth;
                material.uniforms.uPulseExtra.value = pulseExtra;

                const posAttr = geometry.attributes.position;
                const posArr = posAttr.array;
                const offsetAttr = geometry.attributes.aOffsetDir;
                const offsetArr = offsetAttr.array;
                const uvAttr = geometry.attributes.uv;
                const uvArr = uvAttr.array;

                let totalDist = 0;
                distsBuffer[0] = 0;
                const segCount = points.length - 1;

                for (let i = 0; i < points.length; i++) {
                    const { lat, lng } = points[i];
                    const archT = segCount > 0 ? i / segCount : 0;
                    const archR =
                        baseRadius *
                        (1.0 +
                            archHeight * Math.sin(archT * Math.PI) * (1.0 - t));
                    const m = morphedXYZ(lat, lng, archR, t);
                    centersBuffer[i * 3 + 0] = m[0];
                    centersBuffer[i * 3 + 1] = m[1];
                    centersBuffer[i * 3 + 2] = m[2];

                    if (i > 0) {
                        const dx = m[0] - centersBuffer[(i - 1) * 3];
                        const dy = m[1] - centersBuffer[(i - 1) * 3 + 1];
                        const dz = m[2] - centersBuffer[(i - 1) * 3 + 2];
                        totalDist += Math.sqrt(dx * dx + dy * dy + dz * dz);
                    }
                    distsBuffer[i + 1] = totalDist;
                }

                for (let i = 0; i < points.length; i++) {
                    const cx = centersBuffer[i * 3];
                    const cy = centersBuffer[i * 3 + 1];
                    const cz = centersBuffer[i * 3 + 2];

                    if (i < points.length - 1) {
                        _tangent
                            .set(
                                centersBuffer[(i + 1) * 3] - cx,
                                centersBuffer[(i + 1) * 3 + 1] - cy,
                                centersBuffer[(i + 1) * 3 + 2] - cz,
                            )
                            .normalize();
                    }

                    _camDir.set(cx, cy, cz).normalize();
                    _offset.crossVectors(_tangent, _camDir).normalize();

                    const vi = i * 2;
                    posArr[vi * 3 + 0] = cx;
                    posArr[vi * 3 + 1] = cy;
                    posArr[vi * 3 + 2] = cz;
                    offsetArr[vi * 3 + 0] = _offset.x;
                    offsetArr[vi * 3 + 1] = _offset.y;
                    offsetArr[vi * 3 + 2] = _offset.z;

                    posArr[(vi + 1) * 3 + 0] = cx;
                    posArr[(vi + 1) * 3 + 1] = cy;
                    posArr[(vi + 1) * 3 + 2] = cz;
                    offsetArr[(vi + 1) * 3 + 0] = _offset.x;
                    offsetArr[(vi + 1) * 3 + 1] = _offset.y;
                    offsetArr[(vi + 1) * 3 + 2] = _offset.z;

                    const dist = distsBuffer[i];
                    uvArr[i * 4 + 0] = dist;
                    uvArr[i * 4 + 1] = 0;
                    uvArr[i * 4 + 2] = dist;
                    uvArr[i * 4 + 3] = 1;
                }

                posAttr.needsUpdate = true;
                offsetAttr.needsUpdate = true;
                uvAttr.needsUpdate = true;

                line.totalDist = totalDist;
                material.uniforms.uTotalLength.value = totalDist;
                material.uniforms.uPulseWidth.value = totalDist * 0.75;
            }

            for (const line of lineGroup.lines) {
                if (line.lineProgress <= 0.01) {
                    line.material.uniforms.uHeadPos.value = -999.0;
                    continue;
                }
                line.lineTime += d;
                const cycle = LINE_DURATION + LINE_PAUSE;
                const phase = line.lineTime % cycle;
                if (phase < LINE_DURATION) {
                    const pw = line.totalDist * 0.75;
                    line.material.uniforms.uHeadPos.value =
                        (phase / LINE_DURATION) * (line.totalDist + pw * 0.75);
                } else {
                    line.material.uniforms.uHeadPos.value = -999.0;
                }
            }
        }
    }

    _forceDisposeClipCards() {
        for (const card of this._clipCards) {
            for (const mesh of card.meshes) {
                this.markersGroup.remove(mesh);
                mesh.material.dispose();
            }
            if (card.chipMesh) {
                this.markersGroup.remove(card.chipMesh);
                card.chipMesh.material.map?.dispose();
                card.chipMesh.material.dispose();
            }
            if (!card.isPlaceholder) card.thumbnailTexture.dispose();
        }
        this._clipCards = [];
    }

    dispose() {
        this.exitClipMode();
        this._forceDisposeClipCards();
        this._clearMarkers();
        this._clearContinentLines();
        this._markerGeo.dispose();
        this._clipCardGeo.dispose();
        this._chipGeo.dispose();
        if (this._unlockedTexture) this._unlockedTexture.dispose();
        if (this._lockedTexture) this._lockedTexture.dispose();
        if (this._speedTexture) this._speedTexture.dispose();
        if (this._speedHoverTexture) this._speedHoverTexture.dispose();
        if (this._placeholderTexture) this._placeholderTexture.dispose();
    }
}
